import { Injectable, NgZone, inject } from "@angular/core";
import { BaseNotifier } from "../../../../shared/notifiers/base-notifier";
import { Ref } from "../../../../shared/providers/ref";
import {
  FlightState,
  FlightWaypoint,
  FlightDrawTool,
  FlightControlMode,
} from "./flight-state";
import { MapProvider } from "../map/map-provider";
import { FlightService } from "./flight-service";
import MapView from "@arcgis/core/views/MapView";
import SceneView from "@arcgis/core/views/SceneView";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import * as geodeticLengthOperator from "@arcgis/core/geometry/operators/geodeticLengthOperator";
import * as geodeticDensifyOperator from "@arcgis/core/geometry/operators/geodeticDensifyOperator";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import LineSymbol3D from "@arcgis/core/symbols/LineSymbol3D";
import LineSymbol3DLayer from "@arcgis/core/symbols/LineSymbol3DLayer";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Color from "@arcgis/core/Color";
import Camera from "@arcgis/core/Camera";

/** Patrón limpio basado en navegar.html */

class MathUtils {
  static readonly DEG_TO_RAD = Math.PI / 180;
  static readonly RAD_TO_DEG = 180 / Math.PI;
  static readonly METERS_PER_DEG_LAT = 110540;
  static readonly METERS_PER_DEG_LNG = 111320;

  static calculateHeading(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number {
    const dLng = (lng2 - lng1) * this.DEG_TO_RAD;
    const lat1Rad = lat1 * this.DEG_TO_RAD;
    const lat2Rad = lat2 * this.DEG_TO_RAD;
    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x =
      Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
    let heading = Math.atan2(y, x) * this.RAD_TO_DEG;
    return (heading + 360) % 360;
  }

  static getPositionBehind(
    lat: number,
    lng: number,
    heading: number,
    offsetMeters: number,
  ): { lat: number; lng: number } {
    const headingRad = heading * this.DEG_TO_RAD;
    return {
      lat:
        lat - (offsetMeters * Math.cos(headingRad)) / this.METERS_PER_DEG_LAT,
      lng:
        lng -
        (offsetMeters * Math.sin(headingRad)) /
          (this.METERS_PER_DEG_LNG * Math.cos(lat * this.DEG_TO_RAD)),
    };
  }

  static getDistance(
    p1: { lat: number; lng: number },
    p2: { lat: number; lng: number },
  ): number {
    const R = 6371;
    const dLat = (p2.lat - p1.lat) * this.DEG_TO_RAD;
    const dLon = (p2.lng - p1.lng) * this.DEG_TO_RAD;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(p1.lat * this.DEG_TO_RAD) *
        Math.cos(p2.lat * this.DEG_TO_RAD) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  static lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
  }
}

@Injectable()
export class FlightNotifier extends BaseNotifier<FlightState> {
  private static readonly INITIAL: FlightState = {
    activeTool: null,
    controlMode: "route",
    waypoints: [],
    hasRoute: false,
    flightState: "idle",
    currentSegment: 0,
    currentIndex: 0,
    segmentProgress: 0,
    speed: 1,
    isPlaying: false,
    heading: 0,
    autoHeading: true,
    altitude: 0,
    velocity: 0,
    totalDistance: 0,
    distance: 0,
    battery: 100,
    progressPercent: 0,
    followCam: true,
    cameraDistance: 300,
    cameraHeightOffset: 200,
    cameraTilt: 85,
    freeHeading: 0,
    freeAltitude: 1000,
    freeSpeed: 1,
    freeLat: 0,
    freeLng: 0,
    agl: 0,
    terrainFollow: false,
    terrainOffset: 500,
    undergroundMode: false,
    groundOpacity: 1,
    activeKeys: [],
  };

  private static readonly DRONE_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><circle cx="30" cy="30" r="20" fill="rgba(200,220,255,0.5)" stroke="rgba(255,255,255,0.9)" stroke-width="2"/><circle cx="90" cy="30" r="20" fill="rgba(200,220,255,0.5)" stroke="rgba(255,255,255,0.9)" stroke-width="2"/><circle cx="30" cy="90" r="20" fill="rgba(200,220,255,0.5)" stroke="rgba(255,255,255,0.9)" stroke-width="2"/><circle cx="90" cy="90" r="20" fill="rgba(200,220,255,0.5)" stroke="rgba(255,255,255,0.9)" stroke-width="2"/><path d="M30 30 L60 60 L90 30 M30 90 L60 60 L90 90" stroke="%232c3e50" stroke-width="10" fill="none" stroke-linecap="round"/><ellipse cx="60" cy="60" rx="18" ry="22" fill="%2300e5ff" stroke="%23ffffff" stroke-width="3"/><circle cx="60" cy="42" r="7" fill="%23ff3333"/></svg>`;

  private readonly ngZone = inject(NgZone);
  private readonly flightService = inject(FlightService);
  private readonly mapProvider = inject(MapProvider);

  private flightLayer: GraphicsLayer | null = null;
  private droneGraphic: Graphic | null = null;
  private animFrameId: number | null = null;
  private lastTime = 0;

  private readonly FLIGHT_SPEED_BASE = 0.00008; // de navegar.html
  private readonly DENSIFY_METERS = 10;
  private readonly MAX_DENSIFY_POINTS = 1500;
  private readonly CAMERA_OFFSET_METERS = 0;
  private readonly CAMERA_HEIGHT_OFFSET = 200;
  private readonly TILT = 85;
  private readonly FLIGHT_ALT_OFFSET = 450;
  private readonly BATTERY_DRAIN_PER_KM = 0.12;

  constructor(ref: Ref) {
    super(ref, FlightNotifier.INITIAL);
  }

  override build(): void {
    let lastView: MapView | SceneView | null = null;

    this.listen(() => {
      const view = this.mapProvider.notifier.getActiveView();
      if (view && view !== lastView) {
        lastView = view;
        this.flightService.initialize(
          view,
          this.flightService.graphicsLayer || undefined,
        );
      }
    });

    this.listen(() => {
      this.flightService.graphicsVersion();
      if (this.snapshot().controlMode === "route") {
        this._syncFromSketch();
      }
    });
  }

  // ── Sketch Layer (Drawing) ──────────────────────────────────────

  setControlMode(mode: FlightControlMode): void {
    const s = this.snapshot();
    if (s.controlMode === mode) return;
    this.stopFlight();
    this.flightService.deactivate();
    this.setState({ ...s, controlMode: mode, activeTool: null });
    if (mode === "route") {
      this.ensureLayer();
      this.drawRouteGraphics();
    }
  }

  activateTool(tool: FlightDrawTool): void {
    const s = this.snapshot();
    if (s.activeTool === tool) {
      this.setState({ ...s, activeTool: null });
      this.flightService.deactivate();
      return;
    }
    this.setState({ ...s, activeTool: tool });
    this.flightService.activateTool(tool, (graphic?: Graphic) => {
      this.setState({ ...this.snapshot(), activeTool: null });
      if (graphic && graphic.geometry?.type === "polyline") {
        this._processSketchGraphic(graphic);
      } else {
        this._syncFromSketch();
      }
    });
  }

  clearDrawing(): void {
    this.flightService.clear();
    const s = this.snapshot();
    this.setState({
      ...s,
      waypoints: [],
      hasRoute: false,
      flightState: "idle",
      isPlaying: false,
    });
    this.clearFlightGraphics();
  }

  // ── Route Layer (Polyline → Waypoints) ──────────────────────────

  private _syncFromSketch(): void {
    this._processSketchGraphic();
  }

  private async _processSketchGraphic(polyGraphic?: Graphic): Promise<void> {
    const gl = this.flightService.graphicsLayer;
    let poly: Polyline | null = null;

    if (polyGraphic && polyGraphic.geometry?.type === "polyline") {
      poly = polyGraphic.geometry as Polyline;
    } else {
      if (!gl || gl.graphics.length === 0) {
        const s = this.snapshot();
        this.setState({ ...s, waypoints: [], hasRoute: false });
        this.clearFlightGraphics();
        return;
      }
      const polyG = gl.graphics.find((g) => g.geometry?.type === "polyline");
      if (!polyG) return;
      poly = polyG.geometry as Polyline;
    }

    const path = poly.paths?.[0];
    if (!path || path.length < 2) {
      const s = this.snapshot();
      this.setState({ ...s, waypoints: [], hasRoute: false });
      this.clearFlightGraphics();
      return;
    }

    try {
      const resolved = await this.resolveWaypoints(poly);
      const s = this.snapshot();
      this.setState({
        ...s,
        waypoints: resolved,
        hasRoute: true,
        flightState: "idle",
        currentSegment: 0,
        segmentProgress: 0,
        isPlaying: false,
      });
      this.ensureLayer();
      this.drawRouteGraphics();
      this.resetCameraToStart(resolved);
    } catch (e) {
      console.error("[FlightNotifier] _processSketchGraphic error:", e);
      const s = this.snapshot();
      this.setState({ ...s, waypoints: [], hasRoute: false });
    }
  }

  // ── Elevation Sampling (basado en navegar.html: punto por punto) ──

  private async resolveWaypoints(
    polyline: Polyline,
  ): Promise<FlightWaypoint[]> {
    const view = this.mapProvider.notifier.getActiveView() as SceneView | null;
    const path = polyline.paths?.[0] ?? [];
    const sr = polyline.spatialReference;
    const is3857 = sr && (sr.wkid === 102100 || sr.wkid === 3857);

    const toGeographicPath = (path: number[][], is3857: boolean) => {
      const HALF_WORLD = 20037508.342789244;
      const R2D = 180 / Math.PI;
      return path.map((coord: number[]) => {
        let x = coord[0];
        let y = coord[1];
        if (is3857) {
          x = (x / HALF_WORLD) * 180;
          y =
            (Math.atan(Math.exp((y * Math.PI) / HALF_WORLD)) * 2 -
              Math.PI / 2) *
            R2D;
        }
        return [x, y];
      });
    };

    const geoPath = toGeographicPath(path, !!is3857);
    const terrainOffset =
      this.snapshot().terrainOffset ?? this.FLIGHT_ALT_OFFSET;

    // Densify
    const SR = { spatialReference: { wkid: 4326 } };
    const geoPolyline = new Polyline({ paths: [geoPath], ...SR });
    let densified = geoPolyline;

    try {
      // Operadores geodésicos (geometryEngine deprecado desde 4.32)
      if (!geodeticLengthOperator.isLoaded()) {
        await geodeticLengthOperator.load();
      }
      const totalMeters =
        Math.abs(
          geodeticLengthOperator.execute(geoPolyline, { unit: "meters" }),
        ) || 0;
      let step = this.DENSIFY_METERS;
      const approxPoints = Math.ceil(totalMeters / step) + 1;
      if (approxPoints > this.MAX_DENSIFY_POINTS && totalMeters > 0) {
        step = Math.ceil(totalMeters / this.MAX_DENSIFY_POINTS);
      }
      if (!geodeticDensifyOperator.isLoaded()) {
        await geodeticDensifyOperator.load();
      }
      const result = geodeticDensifyOperator.execute(geoPolyline, step);
      if (result && result.type === "polyline") {
        densified = result as Polyline;
      }
    } catch {
      densified = geoPolyline;
    }

    // Query elevation per-point (como navegar.html hace)
    const densPath = densified.paths?.[0] ?? [];
    const promises = densPath.map(async (coord: number[]) => {
      const lng = coord[0];
      const lat = coord[1];
      if (!view?.map?.ground) {
        return { lat, lng, alt: terrainOffset, groundElevation: 0 };
      }
      try {
        const result = await (view.map as any).ground.queryElevation(
          new Point({ longitude: lng, latitude: lat }),
        );
        const elev = Math.max(result?.geometry?.z || 0, 0);
        return { lat, lng, alt: elev + terrainOffset, groundElevation: elev };
      } catch {
        return { lat, lng, alt: terrainOffset, groundElevation: 0 };
      }
    });

    const elevations = await Promise.all(promises);

    // Convert to waypoints
    const waypoints = elevations.map(
      (e: any, i: number) =>
        ({
          id: i,
          lng: e.lng,
          lat: e.lat,
          alt: e.alt,
          name: `Punto ${i + 1}`,
          type:
            i === 0
              ? "Inicio"
              : i === elevations.length - 1
                ? "Fin"
                : "Intermedio",
          groundElevation: e.groundElevation,
          heading: undefined,
        }) as FlightWaypoint,
    );

    console.debug(
      "[FlightNotifier] resolveWaypoints: loaded",
      waypoints.length,
      "waypoints",
    );
    return waypoints;
  }

  // ── Animation Loop (limpio como navegar.html) ──────────────────

  play(): void {
    const s = this.snapshot();
    if (s.flightState === "completed") {
      this.reset();
    }
    const s2 = this.snapshot();
    if (s2.flightState === "idle" || s2.flightState === "paused") {
      this.setState({ ...s2, flightState: "flying", isPlaying: true });
      this.lastTime = 0;
      this.ensureAnim();
    }
  }

  pause(): void {
    const s = this.snapshot();
    if (s.flightState !== "idle" && s.flightState !== "completed") {
      this.setState({
        ...s,
        flightState: "paused",
        isPlaying: false,
        velocity: 0,
      });
      this.stopAnim();
    }
  }

  togglePlay(): void {
    const s = this.snapshot();
    if (s.isPlaying) this.pause();
    else this.play();
  }

  reset(): void {
    this.stopAnim();
    this.lastTime = 0;
    const s = this.snapshot();

    if (s.waypoints.length > 0) {
      this.droneGraphic?.geometry &&
        (this.droneGraphic.geometry = new Point({
          longitude: s.waypoints[0].lng,
          latitude: s.waypoints[0].lat,
          z: s.waypoints[0].alt,
        }));
    }

    this.setState({
      ...s,
      flightState: "idle",
      currentSegment: 0,
      segmentProgress: 0,
      totalDistance: 0,
      altitude: s.waypoints.length > 0 ? s.waypoints[0].alt : 0,
      velocity: 0,
      battery: 100,
      progressPercent: 0,
      isPlaying: false,
    });

    this.drawRouteGraphics();
    this.resetCameraToStart(s.waypoints);
  }

  setSpeed(value: number): void {
    const s = this.snapshot();
    this.setState({ ...s, speed: value });
  }

  setFollowCam(value: boolean): void {
    const s = this.snapshot();
    this.setState({ ...s, followCam: value });
  }

  setAutoHeading(value: boolean): void {
    this.setState({ ...this.snapshot(), autoHeading: value });
  }

  setRouteHeading(value: number): void {
    this.setState({ ...this.snapshot(), heading: (value + 360) % 360 });
  }

  setFreeHeading(value: number): void {
    this.setState({ ...this.snapshot(), freeHeading: (value + 360) % 360 });
  }

  setFreeAltitude(value: number): void {
    this.setState({ ...this.snapshot(), freeAltitude: value });
  }

  setTerrainFollow(value: boolean): void {
    this.setState({ ...this.snapshot(), terrainFollow: value });
  }

  setTerrainOffset(value: number): void {
    this.setState({ ...this.snapshot(), terrainOffset: value });
  }

  setUnderground(value: boolean): void {
    this.setState({ ...this.snapshot(), undergroundMode: value });
  }

  setCameraDistance(value: number): void {
    this.setState({ ...this.snapshot(), cameraDistance: value });
  }

  setCameraHeight(value: number): void {
    this.setState({ ...this.snapshot(), cameraHeightOffset: value });
  }

  setCameraTilt(value: number): void {
    this.setState({ ...this.snapshot(), cameraTilt: value });
  }

  // ── Animation Frame ──────────────────────────────────────────────

  private ensureAnim(): void {
    if (this.animFrameId == null) {
      this.ngZone.runOutsideAngular(() => {
        this.animFrameId = requestAnimationFrame((t) => this._animate(t));
      });
    }
  }

  private stopAnim(): void {
    if (this.animFrameId != null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.lastTime = 0;
  }

  private stopFlight(): void {
    this.stopAnim();
    this.lastTime = 0;
  }

  private _animate(timestamp: number): void {
    const s = this.snapshot();

    if (s.waypoints.length < 2) {
      this.animFrameId = requestAnimationFrame((t) => this._animate(t));
      return;
    }

    if (
      s.flightState === "idle" ||
      s.flightState === "paused" ||
      s.flightState === "completed"
    ) {
      this.animFrameId = requestAnimationFrame((t) => this._animate(t));
      return;
    }

    if (!this.lastTime) this.lastTime = timestamp;
    const deltaTime = timestamp - this.lastTime;
    this.lastTime = timestamp;

    this.handleFlying(deltaTime);
    this.animFrameId = requestAnimationFrame((t) => this._animate(t));
  }

  private handleFlying(deltaTime: number): void {
    const s = this.snapshot();
    if (s.waypoints.length < 2) return;

    const speedMultiplier = s.speed || 1;
    const newProgress =
      s.segmentProgress + this.FLIGHT_SPEED_BASE * speedMultiplier * deltaTime;
    this.setState({
      ...s,
      segmentProgress: newProgress,
    });

    const s2 = this.snapshot();
    if (s2.segmentProgress >= 1) {
      // Completed segment → move to next
      const wp1 = s2.waypoints[s2.currentSegment];
      const wp2 = s2.waypoints[s2.currentSegment + 1];
      const distKm = MathUtils.getDistance(wp1, wp2);

      this.setState({
        ...s2,
        totalDistance: s2.totalDistance + distKm,
        currentSegment: s2.currentSegment + 1,
        segmentProgress: 0,
      });

      const s3 = this.snapshot();
      if (s3.currentSegment >= s3.waypoints.length - 1) {
        this._completeMission();
        return;
      }
    }

    const s4 = this.snapshot();
    const wp1 = s4.waypoints[s4.currentSegment];
    const wp2 = s4.waypoints[s4.currentSegment + 1];
    const curLng = MathUtils.lerp(wp1.lng, wp2.lng, s4.segmentProgress);
    const curLat = MathUtils.lerp(wp1.lat, wp2.lat, s4.segmentProgress);
    const curAlt = MathUtils.lerp(wp1.alt, wp2.alt, s4.segmentProgress);

    if (this.droneGraphic) {
      const view = this.mapProvider.notifier.getActiveView();
      const is3d = view?.type === "3d";
      if (is3d) {
        this.droneGraphic.geometry = new Point({
          longitude: curLng,
          latitude: curLat,
          z: curAlt,
        });
      } else {
        this.droneGraphic.geometry = new Point({
          longitude: curLng,
          latitude: curLat,
        });
      }
    }

    const distKm = MathUtils.getDistance(wp1, wp2);
    const instantSpeed = Math.round(
      (distKm / (this.FLIGHT_SPEED_BASE * speedMultiplier * 40)) * 1.2,
    );
    const battery = Math.max(
      0,
      Math.round(100 - s4.totalDistance * this.BATTERY_DRAIN_PER_KM),
    );

    const flightHeading = MathUtils.calculateHeading(
      wp1.lat,
      wp1.lng,
      wp2.lat,
      wp2.lng,
    );

    this.setState({
      ...s4,
      altitude: curAlt,
      velocity: instantSpeed,
      battery,
      heading: flightHeading,
      progressPercent: Math.round(
        ((s4.currentSegment + s4.segmentProgress) / (s4.waypoints.length - 1)) *
          100,
      ),
    });

    if (s4.followCam) {
      this._setCamera(curLat, curLng, curAlt, flightHeading);
    }
  }

  private _completeMission(): void {
    const s = this.snapshot();
    if (s.waypoints.length > 0) {
      const final = s.waypoints[s.waypoints.length - 1];
      this.setState({
        ...s,
        flightState: "completed",
        altitude: final.alt,
        velocity: 0,
        battery: Math.max(
          0,
          Math.round(100 - s.totalDistance * this.BATTERY_DRAIN_PER_KM),
        ),
        progressPercent: 100,
        isPlaying: false,
      });
    }
    this.stopAnim();
  }

  // ── Graphics ───────────────────────────────────────────────────

  private ensureLayer(): void {
    const view = this.mapProvider.notifier.getActiveView();
    const map = view?.map;
    if (!map) return;

    if (this.flightLayer) {
      try {
        const present = (map.layers as any).find(
          (l: any) => l?.id === this.flightLayer!.id,
        );
        if (!present) {
          map.add(this.flightLayer);
        }
      } catch {}
      return;
    }

    this.flightLayer = new GraphicsLayer({
      id: "flight-layer",
      title: "Vuelo",
      listMode: "hide",
      elevationInfo: { mode: "absolute-height" as const, offset: 0 },
    });
    map.add(this.flightLayer);
  }

  private drawRouteGraphics(): void {
    if (!this.flightLayer) return;
    this.flightLayer.removeAll();

    const s = this.snapshot();
    if (!s.hasRoute || s.waypoints.length < 2) return;

    const view = this.mapProvider.notifier.getActiveView();
    const is3d = view?.type === "3d";
    const SR = { spatialReference: { wkid: 4326 } };

    if (is3d) {
      // 3D route
      this.flightLayer.add(
        new Graphic({
          geometry: new Polyline({
            paths: [s.waypoints.map((w) => [w.lng, w.lat, w.alt])],
            ...SR,
          }),
          symbol: new LineSymbol3D({
            symbolLayers: [
              new LineSymbol3DLayer({
                material: { color: [255, 200, 0, 0.6] },
                size: 8,
              }),
              new LineSymbol3DLayer({
                material: { color: [255, 255, 255, 0.9] },
                size: 4,
              }),
            ],
          }),
        }),
      );
    } else {
      // 2D route
      this.flightLayer.add(
        new Graphic({
          geometry: new Polyline({
            paths: [s.waypoints.map((w) => [w.lng, w.lat])],
            ...SR,
          }),
          symbol: new SimpleLineSymbol({ color: [255, 200, 0, 0.9], width: 4 }),
        }),
      );
    }

    // Drone
    if (s.waypoints.length > 0) {
      if (!this.droneGraphic) {
        const geom = is3d
          ? new Point({
              longitude: s.waypoints[0].lng,
              latitude: s.waypoints[0].lat,
              z: s.waypoints[0].alt,
              ...SR,
            })
          : new Point({
              longitude: s.waypoints[0].lng,
              latitude: s.waypoints[0].lat,
              ...SR,
            });
        this.droneGraphic = new Graphic({
          geometry: geom,
          symbol: new PictureMarkerSymbol({
            url: FlightNotifier.DRONE_SVG,
            width: 50,
            height: 50,
          }),
        });
        this.flightLayer.add(this.droneGraphic);
      }
    }
  }

  private clearFlightGraphics(): void {
    if (this.flightLayer) this.flightLayer.removeAll();
    this.droneGraphic = null;
  }

  private _setCamera(
    lat: number,
    lng: number,
    alt: number,
    heading: number,
  ): void {
    const view = this.mapProvider.notifier.getActiveView() as SceneView | null;
    if (!view) return;

    const behindPos = MathUtils.getPositionBehind(
      lat,
      lng,
      heading,
      this.CAMERA_OFFSET_METERS,
    );
    view.camera = new Camera({
      position: {
        longitude: behindPos.lng,
        latitude: behindPos.lat,
        z: alt + this.CAMERA_HEIGHT_OFFSET,
        spatialReference: { wkid: 4326 },
      },
      heading,
      tilt: this.TILT,
    });
  }

  private resetCameraToStart(waypoints: readonly FlightWaypoint[]): void {
    const view = this.mapProvider.notifier.getActiveView() as SceneView | null;
    if (!view || waypoints.length < 2) return;

    const wp0 = waypoints[0];
    const wp1 = waypoints[1];
    const heading = MathUtils.calculateHeading(
      wp0.lat,
      wp0.lng,
      wp1.lat,
      wp1.lng,
    );

    this._setCamera(wp0.lat, wp0.lng, wp0.alt, heading);
  }
}
